import React from 'react';

import { get as getConstants } from '@lumapps/constants';
import {
    getCurrentContent,
    isNewWriterEditorEnabled,
    isNewSimpleTemplateFeatureEnabled,
    isNewContributionExperienceEnabled,
} from '@lumapps/contents/ducks/selectors';
import { Content, LegacyContentStatus } from '@lumapps/contents/types';
import { useDataAttributes } from '@lumapps/data-attributes';
import { designerContentPreview } from '@lumapps/designer-preview/routes';
import { Menubar } from '@lumapps/lumx-menu/components/Menubar';
import { mdiEye } from '@lumapps/lumx/icons';
import { AlertDialog, Button, IconButton, Kind, Text } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { useResponsive } from '@lumapps/responsive';
import { AppId, useRouter } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { connectedUserSelector } from '@lumapps/user/ducks/selectors';
import { isLayoutEnabled } from '@lumapps/widget-layout/ducks/selectors';

import { SCOPE } from '../../constants';
import { CONTENT_PREVIEW } from '../../keys';

export interface ContentPreviewButtonProps {
    /**
     * Content to preview
     */
    content?: Content;
    /**
     * Save content as draft (needed before previewing)
     */
    saveContentDraft?: (options?: { forPreview?: boolean }) => Promise<Content>;
    /**
     * Wraps the whole preview action when the caller needs to run its own checks before the draft
     * save (e.g. prompting for a mandatory end date). Receiving the action rather than gating the
     * save itself lets the caller abort by never running it, so nothing is left awaiting a save
     * that will not happen. Defaults to running the action straight away.
     */
    guardPreview?: (previewAction: () => unknown) => unknown;
}

/**
 * Is preview unavailable alert dialog open.
 * We need it as a global state because angularjs breaks our internal state on rerender...
 */
let isPreviewUnavailableAlertDialogOpen = false;

const usePreviewUnavailableAlert = () => {
    const [isOpen, setOpen] = React.useState(isPreviewUnavailableAlertDialogOpen);
    const open = React.useCallback(() => {
        isPreviewUnavailableAlertDialogOpen = true;
        setOpen(isPreviewUnavailableAlertDialogOpen);
    }, []);
    const close = React.useCallback(() => {
        isPreviewUnavailableAlertDialogOpen = false;
        setOpen(isPreviewUnavailableAlertDialogOpen);
    }, []);
    return { isOpen, open, close };
};

/**
 * Content preview button
 */
export const ContentPreviewButton: React.FC<ContentPreviewButtonProps> = ({ saveContentDraft, guardPreview }) => {
    const { redirect } = useRouter();
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(SCOPE);
    const { isMobile } = useResponsive();

    const constants = getConstants();
    const isDesignerApp = constants.applicationId === AppId.designer;

    const content = useSelector(getCurrentContent);
    const inputLanguage = useSelector(inputLanguageSelector);
    const user = useSelector(connectedUserSelector);

    const isLayoutFFEnabled = useSelector(isLayoutEnabled);
    const isNewContributionExperienceFFEnabled = useSelector(isNewContributionExperienceEnabled);
    const isNewWriterEditorFFEnabled = useSelector(isNewWriterEditorEnabled);
    const isInNewWriterEditor = isNewWriterEditorFFEnabled && isDesignerApp;
    const isNewSimpleTemplateEnabled = useSelector(isNewSimpleTemplateFeatureEnabled);

    const isSimpleTemplateMode =
        !!content?.template?.basicMode || isNewContributionExperienceFFEnabled || isInNewWriterEditor;

    /**
     * The button should only be displayed if:
     * - User is not an ?as
     * - The layout FF is enabled
     * - The new simple template FF is enabled
     * - We are not in a simple template content (expect if it is a NEW simple template content)
     * - We are in the new writer editor
     */
    const isEnabled =
        (isLayoutFFEnabled && (!isSimpleTemplateMode || isNewSimpleTemplateEnabled)) || isInNewWriterEditor;

    // We display as menu item in last version of new contribution experience (behind the alpha FF for now)
    const isMenuBarItem = isNewContributionExperienceFFEnabled || isInNewWriterEditor;
    // We display as an icon button in simple template responsive and in legacy designer.
    const isIconButton = !isMenuBarItem && (isMobile || !isSimpleTemplateMode);
    // In any other case (ie. desktop + simple template) we display a full button
    const isButton = !isMenuBarItem && !isIconButton;

    const previewUnavailable = usePreviewUnavailableAlert();
    const previewContent = React.useCallback(async () => {
        let contentToPreview = content;

        // Save content as draft (if not "to validate")
        if (saveContentDraft && content?.status !== LegacyContentStatus.toValidate) {
            contentToPreview = await saveContentDraft({ forPreview: true });
        }

        if (!contentToPreview?.template?.isV2Compatible) {
            previewUnavailable.open();
        } else if (contentToPreview) {
            redirect(designerContentPreview(contentToPreview.id!, inputLanguage), {
                // in a new tab
                openInNewTab: true,
            });
        }
    }, [content, saveContentDraft, previewUnavailable, redirect, inputLanguage]);

    const onClick = React.useCallback(
        () => (guardPreview ? guardPreview(previewContent) : previewContent()),
        [guardPreview, previewContent],
    );

    if (!isEnabled) {
        return null;
    }

    // Note: we are not using lokalize key for the as text as it's internal
    const previewLabel = translateKey(
        user?.isConnectedAs ? 'Content preview is disabled when using ?as' : GLOBAL.PREVIEW,
    );

    return (
        <>
            {isButton && (
                <Button
                    emphasis="low"
                    leftIcon={mdiEye}
                    isDisabled={user?.isConnectedAs}
                    onClick={onClick}
                    {...get({ element: 'designer-button' })}
                >
                    {previewLabel}
                </Button>
            )}
            {isIconButton && (
                <IconButton
                    emphasis="low"
                    theme={isSimpleTemplateMode ? 'light' : 'dark'}
                    icon={mdiEye}
                    label={previewLabel}
                    onClick={onClick}
                    isDisabled={user?.isConnectedAs}
                    {...get({ element: 'designer-button' })}
                />
            )}
            {isMenuBarItem && (
                <Menubar.Item
                    tooltipProps={{
                        placement: 'left',
                        label: user?.isConnectedAs ? 'Content preview is disabled when using ?as' : undefined,
                    }}
                    onClick={onClick}
                    icon={mdiEye}
                    isDisabled={user?.isConnectedAs}
                    {...get({ element: 'designer-button' })}
                >
                    {translateKey(GLOBAL.PREVIEW)}
                </Menubar.Item>
            )}
            <AlertDialog
                kind={Kind.warning}
                isOpen={previewUnavailable.isOpen}
                title={translateKey(CONTENT_PREVIEW.PREVIEW_UNAVAILABLE)}
                confirmProps={{
                    onClick: previewUnavailable.close,
                    label: translateKey(GLOBAL.CLOSE),
                }}
            >
                <Text as="p" typography="body1">
                    {translateKey(CONTENT_PREVIEW.PREVIEW_UNAVAILABLE_DESCRIPTION)}
                </Text>
            </AlertDialog>
        </>
    );
};
